import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { deleteEntityDocument } from "../../lib/dataFetchers";
import type { Document } from "../../lib/types";

interface DeleteDocumentDialogProps {
  document: Document;
  entityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteDocumentDialog({
  document,
  entityId,
  open,
  onOpenChange,
}: DeleteDocumentDialogProps): JSX.Element {
  const { t } = useTranslation(["documents", "common"]);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteEntityDocument(entityId, document.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entity-documents", entityId] });
      toast.success(t("documents:delete_success"));
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t("documents:delete_error"));
    },
  });

  function handleClose() {
    if (!mutation.isPending) {
      onOpenChange(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     bg-surface rounded-xl border border-border shadow-card-hov
                     p-6 w-[min(92vw,420px)] focus:outline-none"
          aria-describedby="delete-document-desc"
        >
          <Dialog.Title className="text-base font-semibold text-text mb-1">
            {t("documents:delete_dialog_title")}
          </Dialog.Title>
          <Dialog.Description
            id="delete-document-desc"
            className="text-sm text-text-muted mb-6"
          >
            {t("documents:delete_dialog_description", { name: document.name })}
          </Dialog.Description>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-lg text-sm text-text-muted hover:bg-bg
                         min-h-[44px] focus:outline-none focus:ring-2 focus:ring-border
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common:cancel")}
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white
                         bg-sev-expired hover:bg-sev-expired/90 disabled:opacity-40
                         disabled:cursor-not-allowed min-h-[44px]
                         focus:outline-none focus:ring-2 focus:ring-sev-expired/40"
            >
              {mutation.isPending
                ? t("common:loading")
                : t("documents:delete_confirm_button")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
